import { useInfiniteQuery } from '@tanstack/react-query'
import {
  getCreatedBoxes,
  type CreatedBoxesOrderBy,
  type CreatedBoxesOrderDirection,
} from '../api/userApi'
import { useAppContext } from '../contexts/App.context'
import { DEFAULT_PAGINATION } from '../api/boxApi'
import { useEffect } from 'react'
import { getRefetchInterval } from '../utils/polling'

export interface UseCreatedBoxesParams {
  orderBy?: CreatedBoxesOrderBy
  orderDirection?: CreatedBoxesOrderDirection
  limit?: number
  enabled?: boolean
}

export const useCreatedBoxes = (params: UseCreatedBoxesParams = {}) => {
  const { canFetchUserData } = useAppContext()
  const {
    orderBy,
    orderDirection,
    limit = DEFAULT_PAGINATION.limit,
    enabled = true,
  } = params

  const query = useInfiniteQuery({
    queryKey: ['createdBoxes', orderBy, orderDirection, limit],
    queryFn: async ({ pageParam = 1 }) =>
      await getCreatedBoxes({
        orderBy,
        orderDirection,
        page: pageParam,
        limit,
      }),
    getNextPageParam: lastPage => {
      if (lastPage.meta.totalPages <= lastPage.meta.page) {
        return undefined
      }
      return lastPage.meta.page + 1
    },
    initialPageParam: 1,
    enabled: canFetchUserData && enabled,
    refetchInterval: getRefetchInterval(),
  })

  // Log when canFetchUserData changes to help debug
  useEffect(() => {
    if (enabled && canFetchUserData && !query.isFetching && !query.data) {
      query.refetch()
    }
  }, [canFetchUserData, enabled, query])

  return query
}
